import { inspect } from 'node:util';
import { Logger } from '@gitlab-org/logging';

type CleanUpFunc = () => void;

interface ConsoleMethods {
  log: typeof console.log;
  info: typeof console.info;
  error: typeof console.error;
  warn: typeof console.warn;
  debug: typeof console.debug;
  trace: typeof console.trace;
}

/**
 * Intercepts console methods and process warnings to filter unwanted output
 * and route through Logger.
 *
 * Process warnings (e.g. Node/Bun's NODE_TLS_REJECT_UNAUTHORIZED warning) are
 * printed to stderr by a default `warning` listener. Raw writes bypass Ink's
 * renderer and desync its cursor accounting, leaving stale frames on screen,
 * so the default listener is replaced while the TUI owns the terminal.
 */
export const interceptConsole = (logger: Logger): CleanUpFunc => {
  /* eslint-disable no-console */
  const originalConsole: ConsoleMethods = {
    log: console.log,
    info: console.info,
    error: console.error,
    warn: console.warn,
    debug: console.debug,
    trace: console.trace,
  };

  const serializeArgs = (args: unknown[]): string => {
    return args
      .map((arg) => {
        if (typeof arg === 'string') return arg;
        if (arg instanceof Error) return arg.stack || arg.message;
        return inspect(arg, { depth: 3, breakLength: Infinity, compact: true });
      })
      .join(' ');
  };

  console.log = (...args: unknown[]) => {
    logger.info(`Console: ${serializeArgs(args)}`);
  };

  console.info = (...args: unknown[]) => {
    logger.info(`Console info: ${serializeArgs(args)}`);
  };

  console.error = (...args: unknown[]) => {
    logger.error(`Console error: ${serializeArgs(args)}`);
  };

  console.warn = (...args: unknown[]) => {
    logger.warn(`Console warning: ${serializeArgs(args)}`);
  };

  console.debug = (...args: unknown[]) => {
    logger.debug(`Console debug: ${serializeArgs(args)}`);
  };

  console.trace = (...args: unknown[]) => {
    logger.debug(`Console trace: ${serializeArgs(args)}`);
  };

  const defaultWarningListeners = process.listeners('warning');
  process.removeAllListeners('warning');
  const onProcessWarning = (warning: Error) => {
    logger.warn(`Process warning: ${warning.message}`, warning);
  };
  process.on('warning', onProcessWarning);

  // Return cleanup function to restore original console methods and warning listeners
  return () => {
    console.log = originalConsole.log;
    console.info = originalConsole.info;
    console.error = originalConsole.error;
    console.warn = originalConsole.warn;
    console.debug = originalConsole.debug;
    console.trace = originalConsole.trace;
    process.removeListener('warning', onProcessWarning);
    defaultWarningListeners.forEach((listener) => process.on('warning', listener));
  };
  /* eslint-enable no-console */
};
